/**
 * Image Cycle - Frontend Animation
 *
 * Each image slot cycles through the image pool. A single controller
 * randomly picks which slot to change next.
 */

const SPEED_MAP = {
	slower:   { multiplier: 2, 	 interval: 4000 },
	slow:   	{ multiplier: 1.5, interval: 3000 },
	normal: 	{ multiplier: 1,   interval: 2000 },
	fast:   	{ multiplier: 0.5, interval: 1200 },
};

const RADIUS_PRESETS = [
	'0',
	'100em',
	'100em 0 0 0',
	'0 100em 0 0',
	'0 0 100em 0',
	'0 0 0 100em',
	'100em 0 0 100em',
	'0 100em 100em 0',
	'100em 100em 0 0',
	'0 0 100em 100em',
];

const BREAKPOINT_MEDIUM = 600;
const BREAKPOINT_LARGE = 1080;

const BLOCK_SELECTOR = '.wp-block-amp-child-theme-image-cycle';

function getRandomItem(arr, exclude) {
	const filtered = arr.filter((item) => !exclude.includes(item.url));
	if (filtered.length === 0) return arr[Math.floor(Math.random() * arr.length)];
	return filtered[Math.floor(Math.random() * filtered.length)];
}

function getRandomRadius() {
	return RADIUS_PRESETS[Math.floor(Math.random() * RADIUS_PRESETS.length)];
}

function cycleSlot(slot, images, speedSettings, randomRadius, randomGrayscale, visibleUrls, slotIndex) {
	const img = slot.querySelector('.wp-block-amp-child-theme-image-cycle__image');
	if (!img) return Promise.resolve();

	const { multiplier } = speedSettings;
	const transitionDuration = 700 * multiplier;

	return new Promise(async (resolve) => {
		// Fade out
		img.style.opacity = '0';

		await new Promise((r) => setTimeout(r, transitionDuration));

		// Pick a new random image not currently visible in other slots
		const currentlyVisible = Array.from(visibleUrls);
		const newImage = getRandomItem(images, currentlyVisible);

		// Update tracking
		visibleUrls[slotIndex] = newImage.url;

		// Swap image
		img.src = newImage.url;
		img.alt = newImage.alt || '';

		// Apply random border radius if enabled
		if (randomRadius) {
			img.style.borderRadius = getRandomRadius();
		}

		// Apply random grayscale if enabled
		if (randomGrayscale) {
			img.style.filter = Math.random() < 0.5 ? 'grayscale(100%)' : 'none';
		}

		// Fade in
		img.style.opacity = '1';

		resolve();
	});
}

/**
 * Single controller that randomly picks which slot to cycle next.
 * Tracks recently changed slots to avoid repeating the same one back-to-back.
 */
function startRandomCycler(slots, images, speedSettings, randomRadius, randomGrayscale, visibleUrls) {
	const { interval, multiplier } = speedSettings;
	const transitionDuration = 700 * multiplier;
	const cycleInterval = interval + transitionDuration;
	let lastSlotIndex = -1;

	const pickRandomSlot = () => {
		if (slots.length === 1) return 0;
		let index;
		do {
			index = Math.floor(Math.random() * slots.length);
		} while (index === lastSlotIndex);
		return index;
	};

	const loop = async () => {
		await new Promise((resolve) => setTimeout(resolve, cycleInterval));

		const slotIndex = pickRandomSlot();
		lastSlotIndex = slotIndex;

		await cycleSlot(slots[slotIndex], images, speedSettings, randomRadius, randomGrayscale, visibleUrls, slotIndex);

		loop();
	};

	loop();
}

function initImageCycleBlocks() {
	const blocks = document.querySelectorAll(BLOCK_SELECTOR);

	blocks.forEach((block) => {
		let images;
		try {
			images = JSON.parse(block.dataset.images);
		} catch {
			return;
		}

		if (!Array.isArray(images) || images.length < 2) return;

		const columns = parseInt(block.dataset.columns, 10) || 3;
		const speed = block.dataset.speed || 'normal';
		const randomRadius = block.dataset.randomRadius === 'true';
		const randomGrayscale = block.dataset.randomGrayscale === 'true';
		const orientation = block.dataset.orientation || 'horizontal';
		const justifyContent = block.dataset.justifyContent || 'left';
		const gridGap = block.dataset.gridGap || '';
		const speedSettings = SPEED_MAP[speed] || SPEED_MAP.normal;
		const isVertical = orientation === 'vertical';

		const visibleSm = parseInt(block.dataset.visibleSm, 10) || columns;
		const visibleMd = parseInt(block.dataset.visibleMd, 10) || columns;
		const visibleLg = parseInt(block.dataset.visibleLg, 10) || columns;

		const grid = block.querySelector('.wp-block-amp-child-theme-image-cycle__grid');
		if (!grid) return;

		// Clear the saved HTML and rebuild slots dynamically
		grid.innerHTML = '';

		// Apply layout styles based on orientation
		if (isVertical) {
			grid.style.display = 'flex';
			grid.style.flexDirection = 'column';
			grid.style.gridTemplateColumns = '';
			grid.style.alignItems = justifyContent === 'center' ? 'center'
				: justifyContent === 'right' ? 'flex-end'
				: justifyContent === 'space-between' ? 'stretch'
				: 'flex-start';
		} else {
			grid.style.display = 'grid';
			grid.style.flexDirection = '';
			grid.style.gridTemplateColumns = `repeat(${columns}, 1fr)`;
			grid.style.alignItems = '';
			grid.style.justifyItems = justifyContent === 'center' ? 'center'
				: justifyContent === 'right' ? 'end'
				: justifyContent === 'space-between' ? 'stretch'
				: 'start';
		}

		if (gridGap) {
			grid.style.gap = gridGap;
		}

		// Track which images are currently visible
		const visibleUrls = [];

		// Create exactly `columns` slots with initial images
		for (let i = 0; i < columns; i++) {
			const image = images[i % images.length];
			visibleUrls.push(image.url);

			const slot = document.createElement('div');
			slot.className = 'wp-block-amp-child-theme-image-cycle__slot';

			const img = document.createElement('img');
			img.className = 'wp-block-amp-child-theme-image-cycle__image';
			img.src = image.url;
			img.alt = image.alt || '';

			if (randomRadius) {
				img.style.borderRadius = getRandomRadius();
			}

			if (randomGrayscale) {
				img.style.filter = Math.random() < 0.5 ? 'grayscale(100%)' : 'none';
			}

			slot.appendChild(img);
			grid.appendChild(slot);
		}

		// Set up transitions and initial radius on all slot images
		const slots = Array.from(grid.querySelectorAll('.wp-block-amp-child-theme-image-cycle__slot'));
		const transitionDuration = 700 * speedSettings.multiplier;
		slots.forEach((slot) => {
			const img = slot.querySelector('.wp-block-amp-child-theme-image-cycle__image');
			if (img) {
				img.style.transition = `opacity ${transitionDuration}ms ease`;
			}
		});

		// For vertical orientation, auto-size container width so all images fit perfectly
		if (isVertical) {
			const getVisibleCount = () => {
				const width = window.innerWidth;
				if (width >= BREAKPOINT_LARGE) return visibleLg;
				if (width >= BREAKPOINT_MEDIUM) return visibleMd;
				return visibleSm;
			};

			let lastHeight = 0;
			let lastVisible = 0;
			const autoSizeWidth = () => {
				const containerHeight = block.clientHeight;
				const visible = getVisibleCount();
				if (containerHeight > 0 && (containerHeight !== lastHeight || visible !== lastVisible)) {
					lastHeight = containerHeight;
					lastVisible = visible;
					const gap = parseFloat(getComputedStyle(grid).rowGap) || 0;
					const totalGap = gap * (visible - 1);
					const imageSize = (containerHeight - totalGap) / visible;
					block.style.width = `${imageSize}px`;
				}
			};

			autoSizeWidth();
			new ResizeObserver(autoSizeWidth).observe(block);
			window.addEventListener('resize', autoSizeWidth);
		}

		// Start a single controller that randomly picks which slot to cycle
		startRandomCycler(slots, images, speedSettings, randomRadius, randomGrayscale, visibleUrls);
	});
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initImageCycleBlocks);
} else {
	initImageCycleBlocks();
}
